import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  createCameraSource,
  deleteCameraSource,
  getCameraSourceImage,
  listCameraSources,
  revokeCameraSource,
  updateCameraSource,
} from '../api/cameraSources';
import type {
  CameraSource,
  CameraSourceImage,
  CameraSourceWithToken,
  CreateCameraSourceInput,
  UpdateCameraSourceInput,
} from '../api/cameraSources';

export function cameraSourcesQueryKey(aquariumId: string | undefined): readonly unknown[] {
  return ['camera-sources', aquariumId];
}

export function useCameraSourcesQuery(
  aquariumId: string | undefined,
): UseQueryResult<CameraSource[], Error> {
  return useQuery({
    queryKey: cameraSourcesQueryKey(aquariumId),
    queryFn: () => {
      if (!aquariumId) {
        return Promise.reject(new Error('aquariumId is required to list camera sources'));
      }
      return listCameraSources(aquariumId);
    },
    enabled: Boolean(aquariumId),
  });
}

export function useCreateCameraSourceMutation(
  aquariumId: string | undefined,
): UseMutationResult<CameraSourceWithToken, Error, CreateCameraSourceInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCameraSourceInput) => {
      if (!aquariumId) {
        return Promise.reject(new Error('aquariumId is required to create a camera source'));
      }
      return createCameraSource(aquariumId, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cameraSourcesQueryKey(aquariumId) });
    },
  });
}

export function useUpdateCameraSourceMutation(
  aquariumId: string | undefined,
): UseMutationResult<CameraSource, Error, { id: string; input: UpdateCameraSourceInput }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCameraSourceInput }) => {
      if (!aquariumId) {
        return Promise.reject(new Error('aquariumId is required to update a camera source'));
      }
      return updateCameraSource(aquariumId, id, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cameraSourcesQueryKey(aquariumId) });
    },
  });
}

export function useRevokeCameraSourceMutation(
  aquariumId: string | undefined,
): UseMutationResult<CameraSource, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (!aquariumId) {
        return Promise.reject(new Error('aquariumId is required to revoke a camera source'));
      }
      return revokeCameraSource(aquariumId, id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cameraSourcesQueryKey(aquariumId) });
    },
  });
}

export function useDeleteCameraSourceMutation(
  aquariumId: string | undefined,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (!aquariumId) {
        return Promise.reject(new Error('aquariumId is required to delete a camera source'));
      }
      return deleteCameraSource(aquariumId, id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cameraSourcesQueryKey(aquariumId) });
    },
  });
}

/**
 * Loads each source's exact last received image by id (one request per
 * distinct image id, cached per id). Returns a map imageId → image; ids whose
 * request is pending or failed (e.g. the image was deleted) are simply absent.
 */
export function useCameraSourceImages(
  aquariumId: string | undefined,
  imageIds: readonly string[],
): Map<string, CameraSourceImage> {
  const uniqueIds = Array.from(new Set(imageIds));

  return useQueries({
    queries: uniqueIds.map((imageId) => ({
      queryKey: [...cameraSourcesQueryKey(aquariumId), 'image', imageId],
      queryFn: () => getCameraSourceImage(aquariumId as string, imageId),
      enabled: Boolean(aquariumId),
      retry: false,
    })),
    combine: (results) => {
      const byId = new Map<string, CameraSourceImage>();
      results.forEach((result, index) => {
        const imageId = uniqueIds[index];
        if (imageId && result.data) byId.set(imageId, result.data);
      });
      return byId;
    },
  });
}
